<template>
  <div class="my-10">
    <Modal :show-modal="showModal" @cancel="closeModal">
      <template v-slot:header>{{ $t('page.buyService') }}</template>
      <template v-slot:content>
        <Form :validation-schema="schemaService()">
          <div
            v-if="
              selectedPricing &&
              selectedPricing.type !== 'replace' &&
              selectedPricing.type !== 'edit'
            "
            class="mb-4 flex flex-col gap-2"
          >
            <label class="text-start font-bold" for="quantity">{{
              $t('page.enterDayAmount')
            }}</label>
            <Field
              class="text-black cursor-pointer p-2 w-full border rounded"
              v-model="serviceForm.quantity"
              type="number"
              name="quantity"
              id="quantity"
            ></Field>
            <ErrorMessage name="quantity" class="text-red-500 text-start" />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-start font-bold" for="bank">{{ $t('page.selectBank') }}</label>
            <p class="text-start">{{ getBankDescription }}</p>
            <p class="text-start">{{ getPaymentDescription }}</p>
            <Field
              v-model="serviceForm.bank"
              id="bank"
              name="bank"
              class="text-black cursor-pointer p-2 w-full rounded"
              as="select"
            >
              <option v-for="bank in banks" :key="bank.value" :value="bank.value">
                {{ bank.name[$i18n.locale] }}
              </option>
            </Field>
            <ErrorMessage name="bank" class="text-red-500 text-start" />
          </div>
          <SubmitBtn
            @click.prevent="buyStatus"
            type="submit"
            class="!w-full mt-5 rounded !bg-green-600 text-white font-semibold text-lg hover:!bg-green-800"
            >{{ $t('page.buy') }}</SubmitBtn
          >
        </Form>
      </template>
    </Modal>
    <div class="flex flex-col gap-10">
      <div class="grid grid-cols-2 gap-5 max-lg:grid-cols-1">
        <div class="flex flex-col">
          <div>
            <video
              controlsList="nodownload"
              v-if="isVideo(currentImage)"
              controls
              :src="currentImage"
              class="object-contain h-[25rem] w-full"
            ></video>
            <img
              v-else
              :src="currentImage || placeholderImage"
              alt="main-image"
              class="w-full h-[25rem] object-fill rounded-lg cursor-pointer"
              @click="showSingle"
            />
            <vue-easy-lightbox
              :imgs="currentImage"
              :index="indexRef"
              :visible="visibleRef"
              @hide="onHide"
            ></vue-easy-lightbox>
          </div>
          <div v-if="this.detail?.images?.length">
            <Splide :has-track="false" :options="imageOptions" aria-label="carouselBanner" class="">
              <SplideTrack class="rounded-lg py-2">
                <SplideSlide v-for="(item, index) in detail.images" :key="item.id" class="">
                  <video
                    @click="changeImage(index)"
                    controlsList="nodownload"
                    v-if="isVideo(item?.path)"
                    :src="item?.path"
                    class="h-32 object-cover rounded-md cursor-pointer w-full"
                  ></video>
                  <img
                    v-else
                    :src="item.path || placeholderImage"
                    alt=""
                    class="h-32 w-full object-contain rounded-md cursor-pointer"
                    @click="changeImage(index)"
                  />
                </SplideSlide>
              </SplideTrack>
            </Splide>
          </div>
        </div>
        <div
          :class="{ 'bg-gradient-to-br from-[#ECECFE] to-[#D3D5FD] p-2 rounded': detail?.is_vip }"
        >
          <div class="border-b border-[#E3E3E3] mb-4">
            <div class="flex items-center gap-3 mb-3 min-w-0">
              <p
                v-if="detail?.name"
                class="flex-1 min-w-0 text-main-color font-bold text-3xl truncate"
                :title="detail?.name"
              >
                {{ detail?.name }}
              </p>

              <p
                v-else
                class="flex-1 min-w-0 text-main-color font-bold text-3xl truncate"
                :title="(detail?.title_properties || []).map(property => property.value).join(' ')"
              >
                <span v-for="(property, idx) in detail?.title_properties" :key="idx">
                  {{ property.value }}
                </span>
              </p>
            </div>
            <div class="mb-2">
              <p
                v-if="detail?.status == 2"
                class="bg-red-500 py-1 px-2 text-white font-medium w-max rounded"
              >
                {{ $t('page.expired') }}
              </p>
            </div>
            <div class="mb-2">
              <p>
                {{ $t('page.category') }}:
                <router-link
                  class="text-lg font-medium hover:text-accent-color"
                  :to="{ name: 'publications', query: { category: detail?.category_id } }"
                  >{{ detail?.category }}</router-link
                >
              </p>
            </div>
            <div class="flex items-center gap-5 mb-3">
              <p class="text-[#409DBA] font-semibold text-2xl">
                {{ formatNumber(detail?.price) }} TMT
              </p>
              <div class="flex items-center gap-1">
                <IconEye></IconEye>
                <p class="text-main-color font-semibold text-sm">{{ detail?.view_count }}</p>
              </div>
            </div>

            <div class="flex mb-2 font-light text-main-color text-base">
              <p>{{ detail?.location }}</p>
              <div class="flex items-center">
                <div class="border-l mx-2 h-5"></div>
                <p>{{ detail?.date_created }}</p>
              </div>
            </div>
            <div class="mb-2">
              <a class="hover:text-accent-color" :href="`tel:${detail?.phone}`">{{
                detail?.phone
              }}</a>
            </div>
            <p v-if="detail?.id" class="mb-2 font-bold">ID: {{ detail?.id }}</p>
          </div>

          <div class="w-full">
            <div class="flex flex-col gap-3 mb-4">
              <div v-if="categoryDetail?.on_credit" class="flex items-center gap-2">
                {{ $t('page.onCredit') }} <IconChecked v-if="detail?.on_credit" />
                <IconUnchecked v-else />
              </div>
              <div v-if="categoryDetail?.on_exchange" class="flex items-center gap-2">
                {{ $t('page.onExchange') }} <IconChecked v-if="detail?.on_exchange" />
                <IconUnchecked v-else />
              </div>
              <div class="flex items-center gap-2">
                {{ $t('page.allowComments') }} <IconChecked v-if="detail?.allow_comments" />
                <IconUnchecked v-else />
              </div>
            </div>

            <p class="font-light text-[#333333] text-lg w-96 mb-5">
              {{ detail?.description }}
            </p>
            <div class="flex justify-end gap-5 mb-3">
              <button
                v-if="detail?.subscription !== ''"
                class="w-max rounded-lg px-8 font-bold"
                :class="getSubcriptionType?.color"
              >
                {{ getSubcriptionType?.name }}
              </button>
              <div
                v-if="detail?.is_vip"
                class="w-max px-8 bg-[#7951F9] rounded-lg text-white font-bold text-2xl text-end"
              >
                VIP
              </div>
            </div>
            <div class="bg-white divide-y rounded mb-5">
              <p class="text-main-color text-base font-semibold p-4">{{ $t('page.detail') }}</p>
              <InfoBlock :attributes="detail?.attributes" />
            </div>
            <button
              @click="deletePublication"
              class="bg-red-500 text-white w-full rounded py-3 hover:bg-red-600"
            >
              {{ $t('page.remove') }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-5">
        <h2 class="font-bold text-2xl">{{ $t('page.freeServices') }}</h2>

        <!-- Если объявление просрочено: показываем только повторное размещение -->
        <div v-if="detail?.status == 2" class="grid grid-cols-2 gap-10 max-lg:grid-cols-1">
          <div class="p-5 bg-white rounded-lg">
            <p class="font-bold text-xl">{{ $t('page.restore') }}</p>
            <p>{{ $t('page.expired') }}</p>

            <div class="mt-4 flex justify-between items-center">
              <p class="font-bold text-lg italic text-accent-color">{{ $t('page.free') }}</p>

              <button
                :disabled="isResetting"
                class="w-max p-2.5 px-5 bg-main-color text-white font-semibold rounded disabled:bg-gray-400 hover:bg-accent-color"
                @click="resetPublication"
              >
                {{ $t('page.republishFree') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Если объявление активное: оставляем старые бесплатные услуги -->
        <div v-else class="grid grid-cols-2 gap-10 max-lg:grid-cols-1">
          <!-- EDIT CARD -->
          <div class="p-5 bg-white rounded-lg">
            <p class="font-bold text-xl">{{ $t('page.edit') }}</p>
            <p>{{ $store?.setting?.business_publication_free_edit_description }}</p>

            <div class="mt-4 flex justify-between items-center">
              <p class="font-bold text-lg italic text-accent-color">{{ $t('page.free') }}</p>

              <div class="flex flex-col items-end gap-1">
                <button
                  :disabled="!detail?.can_edit"
                  class="w-max p-2.5 px-5 bg-main-color text-white font-semibold rounded disabled:bg-gray-400 hover:bg-accent-color"
                  @click="toEdit"
                >
                  {{ $t('page.addEdit') }}
                </button>

                <p v-if="detail && !detail?.can_edit" class="text-xs text-gray-500 text-right">
                  <template v-if="detail?.date_can_edit">
                    {{ $t('warning.CAN_EDIT_AFTER') }}: {{ detail?.date_can_edit }}
                  </template>
                  <template v-else>
                    {{ $t('warning.NO_EDITS_LEFT') }}
                  </template>
                </p>
              </div>
            </div>
          </div>

          <!-- RAISE CARD -->
          <div class="p-5 bg-white rounded-lg">
            <p class="font-bold text-xl">{{ $t('page.raise') }}</p>
            <p>{{ $store?.setting?.business_publication_free_raise_description }}</p>

            <div class="mt-4 flex justify-between items-center">
              <p class="font-bold text-lg italic text-accent-color">{{ $t('page.free') }}</p>

              <div class="flex flex-col items-end gap-1">
                <button
                  :disabled="!detail?.can_raise"
                  class="w-max p-2.5 px-5 bg-main-color text-white font-semibold rounded disabled:bg-gray-400 hover:bg-accent-color"
                  @click="raisePublication"
                >
                  {{ $t('page.addRaise') }}
                </button>

                <p v-if="detail && !detail?.can_raise" class="text-xs text-gray-500 text-right">
                  <template v-if="detail?.date_can_raise">
                    {{ $t('warning.CAN_RAISE_AFTER') }}: {{ detail?.date_can_raise }}
                  </template>
                  <template v-else>
                    {{ $t('warning.NO_RAISES_LEFT') }}
                  </template>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-5">
        <h2 class="font-bold text-2xl">{{ $t('page.pricedServices') }}</h2>
        <div class="relative">
          <div
            v-if="detail?.status == 2"
            style="background-color: #ffffffa6; backdrop-filter: saturate(180%) blur(2px)"
            class="absolute top-0 left-0 w-full h-full pointer-events-none"
          ></div>
          <div class="grid grid-cols-2 gap-10 max-lg:grid-cols-1">
            <div v-for="item in pricing" :key="item?.id" class="bg-white p-5 rounded-lg flex gap-5">
              <div>
                <img :src="item?.image" alt="" class="aspect-square w-44 object-cover rounded-xl" />
              </div>
              <div class="flex flex-col gap-2 w-full">
                <p class="font-bold text-xl">{{ item?.name }}</p>
                <p>{{ item?.description }}</p>
                <div class="mt-auto flex gap-2 w-full">
                  <p class="w-full mr-5 font-bold text-accent-color text-lg">
                    {{ item?.price }} TMT
                  </p>
                  <button
                    :disabled="detail?.status == 2"
                    class="w-full rounded p-1 bg-green-600 text-white font-semibold text-lg hover:!bg-green-800 disabled:bg-gray-400 disabled:hover:!bg-gray-400"
                    @click="detail?.status == 2 ? null : selectPricing(item)"
                  >
                    {{ $t('page.buy') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import api from '@/api';
import Modal from '@cmp/Modal.vue';
import VueEasyLightbox from 'vue-easy-lightbox';
import { schemaService } from '@/schemas';

import IconChecked from '@icons/IconChecked.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import IconEye from '@icons/IconEye.vue';
import { i18n } from '@/i18n';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { formatDate, formattedHour, formatNumber } from '@/helpers';
import { Field, Form, ErrorMessage } from 'vee-validate';
import { isVideo } from '@/helpers';
import InfoBlock from '@cmp/InfoBlock.vue';

export default {
  name: 'viewMyPublication',
  components: {
    Modal,
    VueEasyLightbox,
    Splide,
    SplideSlide,
    SplideTrack,
    IconEye,
    IconChecked,
    IconUnchecked,
    Field,
    Form,
    ErrorMessage,
    InfoBlock,
  },
  data() {
    return {
      loading: false,
      isResetting: false,
      redirectUrl: null,
      schemaService: schemaService,
      currentId: null,
      detail: null,
      pricing: [],
      selectedPricing: null,
      showModal: false,
      visibleRef: false,
      indexRef: 0,
      imgsRef: [],
      currentImage: null,
      categoryDetail: {},
      placeholderImage: '/placeholder-publication.png',

      banks: [
        {
          name: { tk: 'Beýleki banklar', ru: 'Другие банки', en: 'Other banks' },
          value: 0,
        },
        { name: { tk: 'Rysgal', ru: 'Рысгал', en: 'Rysgal' }, value: 1 },
        { name: { tk: 'Senagat', ru: 'Сенагат', en: 'Senagat' }, value: 2 },
        { name: { tk: 'TFEB', ru: 'Внешнеэконом банк', en: 'TFEB' }, value: 3 },
      ],
      serviceForm: {
        service: undefined,
        bank: undefined,
        quantity: 1,
      },
    };
  },
  methods: {
    isVideo,
    formatDate,
    formattedHour,
    formatNumber,
    onShow() {
      this.visibleRef = true;
      this.imgsRef = this.detail.images;
    },
    onHide() {
      this.visibleRef = false;
    },
    showSingle() {
      if (!this.detail?.images?.length) return;
      this.onShow();
    },
    changeImage(index) {
      this.currentImage =
        this.detail?.images?.[index]?.path || this.detail?.thumbnail || this.placeholderImage;
    },
    toEdit() {
      this.$router.push({
        name: 'editMyBusinessPublication',
        params: { id: this.currentId },
      });
    },
    closeModal() {
      this.showModal = false;
    },
    openModal() {
      this.showModal = true;
    },
    selectPricing(val) {
      this.selectedPricing = val;
      this.serviceForm.service = val.id;
      this.openModal();
    },

    async getMyPublication() {
      try {
        const response = await api.get(`publications/my-publications/${this.currentId}/`);
        this.detail = response.data;

        if (this.detail?.images?.length > 0) {
          this.currentImage =
            this.detail.images[0]?.path || this.detail?.thumbnail || this.placeholderImage;
        } else {
          this.currentImage = this.detail?.thumbnail || this.placeholderImage;
        }

        await this.getCategoryDetail();
      } catch (e) {
        console.log(e);
      }
    },

    async getCategoryDetail() {
      try {
        const response = await api.get(`publications/categories/${this.detail.category_id}/`);
        this.categoryDetail = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    async deletePublication() {
      try {
        const response = await api.delete(`publications/my-publications/${this.currentId}/`);
        this.$toast.info(i18n.global.t('warning.PUBLICATION_REMOVED'));
        this.$router.push({ name: 'myBusinessPublications' });
      } catch (e) {
        console.log(e);
      }
    },
    async buyStatus() {
      try {
        const body = {
          bank: this.serviceForm.bank,
          purchases: [
            {
              quantity: this.serviceForm.quantity,
              service: this.serviceForm.service,
            },
          ],
        };
        const response = await api.post(
          `publications/my-publications/${this.currentId}/buy/`,
          body,
        );
        let responseOK = response && response.status === 200;
        this.closeModal();
        if (responseOK) {
          this.redirectUrl = response.data?.form_url;

          if (!this.redirectUrl) {
            this.$toast.error('form_url не пришёл от сервера');
            return;
          }

          this.$toast.info(i18n.global.t('warning.TO_BANK_PAGE'));

          setTimeout(() => {
            this.loading = true;
            window.location.href = this.redirectUrl;
          }, 3000);
        }
      } catch (e) {
        console.log(e);
      }
    },
    async getPricing() {
      try {
        const response = await api.get('commercials/service_pricing/');
        this.pricing = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    async raisePublication() {
      try {
        const response = await api.post(
          `publications/my-publications/${this.currentId}/raise_it/`,
          {},
        );
        this.$toast.info(i18n.global.t('warning.PUBLICATION_RAISED'));
        this.getMyPublication();
      } catch (e) {
        console.log(e);
      }
    },

    async resetPublication() {
      if (this.isResetting) return;

      this.isResetting = true;

      try {
        const response = await api.post(
          `publications/my-publications/${this.currentId}/reset/`,
          {},
        );

        if (response?.status === 200) {
          this.$toast.info(i18n.global.t('warning.PUBLICATION_RESTORED'));
          await this.getMyPublication();
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.isResetting = false;
      }
    },
  },

  computed: {
    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          break;
      }
    },
    canRaisePublication() {
      var providedDate = new Date(this.detail?.date_can_raise);

      var currentDate = new Date();

      // Compare the two dates
      if (providedDate < currentDate) {
        return true;
      } else {
        return false;
      }
    },
    getBankDescription() {
      return this.$store?.setting?.bank_description;
    },
    getPaymentDescription() {
      return this.$store?.setting?.payment_description;
    },
    canEditPublication() {
      var providedDate = new Date(this.detail?.date_can_edit);

      var currentDate = new Date();

      if (providedDate < currentDate) {
        return true;
      } else {
        return false;
      }
    },
  },
  created() {
    this.currentId = this.$route.params.id;
    this.getMyPublication();
    this.getPricing();
  },
};
</script>

<style scoped></style>
